import { useCallback, useRef, useState } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { ExperiencePerformanceTypes, UFOExperience, ExperienceTypes } from '@atlassian/ufo';
import { PACKAGE_NAME } from '../../common/constants.tsx';
import {
	type SampleProjectServiceInitialState,
	type CreateJwmSampleProjectStatusResponse,
	TASK_RUNNER_TASK_STATE,
} from './types.tsx';

const CREATE_JWM_SAMPLE_PROJECT_ENDPOINT = '/rest/internal/latest/jwm/sample-project';
const CREATE_JWM_SAMPLE_PROJECT_PROGRESS_UPDATE_ENDPOINT =
	'/rest/internal/latest/jwm/sample-project/progress/';

export const REQUEST_STATUS_UPDATE_INTERVAL = 1000;

export const createSampleProjectOperation = new UFOExperience('business-sample-project-create', {
	type: ExperienceTypes.Operation,
	performanceType: ExperiencePerformanceTypes.Custom,
});

export const useCreateJwmSampleProjectService = () => {
	const [state, setState] = useState<SampleProjectServiceInitialState>({
		loading: false,
		error: null,
		taskId: null,
	});

	const createSampleProject = useCallback(async () => {
		createSampleProjectOperation.start();

		setState({
			loading: true,
			error: null,
			taskId: null,
		});

		try {
			const taskId = await performPostRequest(CREATE_JWM_SAMPLE_PROJECT_ENDPOINT).then(
				(resp) => resp.taskId,
			);
			setState({
				loading: false,
				error: null,
				taskId,
			});

			createSampleProjectOperation.success();
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (err: any) {
			fireErrorAnalytics({
				meta: {
					id: 'createJwmSampleProjectService',
					packageName: PACKAGE_NAME,
					teamName: 'jira-werewolves',
				},
				error: err,
				sendToPrivacyUnsafeSplunk: true,
			});
			setState({
				loading: false,
				error: err,
				taskId: null,
			});

			createSampleProjectOperation.failure();
		}
	}, []);

	return { ...state, createSampleProject };
};

export const requestStatusUpdate = (taskId: string) =>
	fetchJson<CreateJwmSampleProjectStatusResponse>(
		CREATE_JWM_SAMPLE_PROJECT_PROGRESS_UPDATE_ENDPOINT + taskId,
	);

const shouldContinuePolling = (status: TASK_RUNNER_TASK_STATE) => {
	switch (status) {
		case TASK_RUNNER_TASK_STATE.RUNNING:
		case TASK_RUNNER_TASK_STATE.ENQUEUED:
			return true;
		case TASK_RUNNER_TASK_STATE.COMPLETE:
		case TASK_RUNNER_TASK_STATE.FAILED:
		case TASK_RUNNER_TASK_STATE.CANCEL_REQUESTED:
		case TASK_RUNNER_TASK_STATE.DEAD:
		default:
			return false;
	}
};

const projectCreateFailed = (status: TASK_RUNNER_TASK_STATE) =>
	status === TASK_RUNNER_TASK_STATE.FAILED || status === TASK_RUNNER_TASK_STATE.DEAD;

export const useCreateJwmSampleProjectProgressTrackingService = (taskId: string) => {
	const [progress, setProgress] = useState<CreateJwmSampleProjectStatusResponse | null>(null);
	const timer = useRef<NodeJS.Timeout | null>(null);
	const [error, setError] = useState<Error | null>(null);

	const onFailure = (errorMsg: string) => {
		const err = new Error(errorMsg);
		fireErrorAnalytics({
			meta: {
				id: 'createJwmSampleProjectProgressTrackingService',
				packageName: PACKAGE_NAME,
				teamName: 'jira-werewolves',
			},
			error: err,
			sendToPrivacyUnsafeSplunk: true,
		});
		setError(err);
	};

	const trackStatusUpdates = useCallback(() => {
		if (!taskId) {
			return;
		}
		if (timer.current) {
			return;
		}
		timer.current = setInterval(async () => {
			try {
				const progressResponse = await requestStatusUpdate(taskId);
				if (!shouldContinuePolling(progressResponse.status) && timer?.current) {
					clearInterval(timer.current);
				}
				if (projectCreateFailed(progressResponse?.status)) {
					onFailure('Creating JWM sample project failed');
				}
				if (progressResponse.progressPercentage !== progress?.progressPercentage) {
					setProgress(progressResponse);
				}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				if (timer.current) {
					clearInterval(timer.current);
				}
				onFailure(`Fetching project creation progress failed: ${err.message}`);
			}
		}, REQUEST_STATUS_UPDATE_INTERVAL);
	}, [progress?.progressPercentage, taskId]);

	return {
		trackStatusUpdates,
		progressPercentage: progress?.progressPercentage ?? 0,
		projectKey: progress?.projectKey ?? null,
		error,
	};
};
